import { useEffect, useMemo, useRef, useState } from 'react';
import useGameStore, { districtSetOf, ownershipPct, rentFor, rentYieldOf, valuationOf } from '../store/gameStore';
import type { MarketPhase, PropertyTile } from '../types/gameTypes';
import { buildPerimeterLayout } from './boardLayout';
import { money } from '../lib/format';
import { PlayerPawn } from './PlayerPawn';
import TurnBanner from './TurnBanner';
import TileInspector, { DeltaTag } from './TileInspector';
import { TradeSharesModal } from './TradeSharesModal';
import EmergencyModal from './EmergencyModal';
import { AuctionPanel, OfferChoiceCard } from './AuctionCards';
import { BlackSwanBanner, FastTravelPanel, LobbyPanel, TakeoverModal } from './StrategyPanels';
import { AttentionModal } from './TradeModal';
import BotStatusBar from './BotStatusBar';
import TradeToast from './TradeToast';
import BotPurchaseToast from './BotPurchaseToast';
import PlayerLedgerHud from './PlayerLedgerHud';
import MoneyTransferOverlay from './MoneyTransferOverlay';
import { DISTRICT_META } from './districtMeta';
import { tileName as localizeTile, useT } from '../i18n';

const STEP_DURATION_MS = 260;
const MAX_ANIMATED_STEPS = 12; // any longer jump (e.g. board reset) snaps instead of walking

const heatValue = (tile: PropertyTile, phase: MarketPhase) => {
  if (tile.tileType === 'COMMERCIAL') {
    const float = tile.totalShares - Object.values(tile.shares).reduce((sum, value) => sum + value, 0);
    return float > 0 ? valuationOf(tile, phase) * 0.2 : rentFor(tile, phase) * 6;
  }
  return tile.tileType === 'TRANSIT' ? 60 : 0;
};

const tileSubtitle = (tile: PropertyTile, rent: number) => {
  if (tile.tileType === 'COMMERCIAL')
    return `Rent ${money(rent)} (yield ${(rentYieldOf(tile) * 100).toFixed(1)}%) · ${tile.totalShares} shares`;
  if (tile.tileType === 'TRANSIT') return 'Transit dividend $60 · Metro fast-travel';
  if (tile.tileType === 'FINANCIAL') return 'Financial desk · borrow / float / insure';
  if (tile.name.includes('Payroll')) return 'Payroll payout $2,000';
  if (tile.name.includes('Audit')) return 'City Hall · lobbying & audits';
  return 'Special tile';
};

interface GameBoardProps {
  heatmap: boolean;
}

/** True on compact viewports (phones / portrait tablets — matches the App mobile branch). */
const useCompactViewport = (): boolean => {
  const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = () => setCompact(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return compact;
};

const GameBoard = ({ heatmap }: GameBoardProps) => {
  const store = useGameStore();
  const t = useT();
  const compact = useCompactViewport();
  const {
    board,
    players,
    market,
    currentPlayerIndex,
    turnPhase,
    selectedTileIndex,
    idlePenalty,
    reachableIndices,
    lastRentCredit,
  } = store;

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [inspectedIndex, setInspectedIndex] = useState<number | null>(null);
  // Closes both the local inspector and the sidebar-driven one.
  const closeInspector = () => {
    setInspectedIndex(null);
    store.setInspectTile(null);
  };
  const [trade, setTrade] = useState<{ tileId: string; mode: 'BUY' | 'SELL' } | null>(null);
  const openTrade = (tileId: string, mode: 'BUY' | 'SELL') => { setInspectedIndex(null); setTrade({ tileId, mode }); };
  const [rentToast, setRentToast] = useState<{ amount: number; from: string; tileIndex: number; key: number } | null>(null);
  const [pulseTile, setPulseTile] = useState<number | null>(null);
  const [cashFlash, setCashFlash] = useState(false);
  // Per-player visual tile index: walks tile by tile behind the store position so
  // a roll reads as a step-by-step animation instead of a teleport.
  const [displayIndex, setDisplayIndex] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    players.forEach((player) => {
      initial[player.id] = player.currentTileIndex;
    });
    return initial;
  });

  const active = players[currentPlayerIndex];
  const isHumanTurn = Boolean(active && !active.isAI);
  // Board-aware layout: the four corner landmarks stay pinned to the corners even
  // after annexations grow the ring (28 → 30 → 32 tiles …) — no drift, no overlap.
  const layout = useMemo(() => buildPerimeterLayout(board.length, board), [board.length, board]);
  // Group pawns per displayed tile so 10 players sharing one tile stack instead of overflowing.
  const groupedPawns = useMemo(() => {
    const groups = new Map<number, { player: (typeof players)[number]; shown: number; isStepping: boolean }[]>();
    players.forEach((player) => {
      const shown = displayIndex[player.id] ?? player.currentTileIndex;
      const list = groups.get(shown) ?? [];
      list.push({ player, shown, isStepping: shown !== player.currentTileIndex });
      groups.set(shown, list);
    });
    return [...groups.entries()].map(([tileIndex, members]) => ({ tileIndex, members }));
  }, [players, displayIndex]);
  const positionKey = players.map((player) => `${player.id}:${player.currentTileIndex}`).join('|');
  const human = players[0];

  // Rent inflow feedback: any bot landing on human-owned shares pulses the landed
  // tile, floats a cash toast and flashes the portfolio cash readout.
  const seenRentSeq = useRef(0);
  useEffect(() => {
    if (!lastRentCredit || !human || lastRentCredit.seq <= seenRentSeq.current) return;
    seenRentSeq.current = lastRentCredit.seq;
    const myCredit = lastRentCredit.credits.find((c) => c.playerId === human.id);
    if (!myCredit || myCredit.amount <= 0) return;
    setRentToast({ amount: myCredit.amount, from: lastRentCredit.payerName, tileIndex: lastRentCredit.tileIndex, key: lastRentCredit.seq });
    setPulseTile(lastRentCredit.tileIndex);
    setCashFlash(true);
    const t1 = window.setTimeout(() => setRentToast(null), 2600);
    const t2 = window.setTimeout(() => setPulseTile(null), 2000);
    const t3 = window.setTimeout(() => setCashFlash(false), 1400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [lastRentCredit, human]);

  useEffect(() => {
    const timers: number[] = [];
    let snap: Record<string, number> | null = null;

    players.forEach((player) => {
      const from = displayIndex[player.id];
      if (from === undefined || from === player.currentTileIndex) return;

      const distance = (player.currentTileIndex - from + board.length) % board.length;
      if (distance > MAX_ANIMATED_STEPS) {
        snap = { ...(snap ?? {}), [player.id]: player.currentTileIndex };
        return;
      }

      const next = (from + 1) % board.length;
      timers.push(
        window.setTimeout(() => {
          setDisplayIndex((prev) => ({ ...prev, [player.id]: next }));
        }, STEP_DURATION_MS),
      );
    });

    if (snap) {
      const resolved: Record<string, number> = snap;
      setDisplayIndex((prev) => ({ ...prev, ...resolved }));
    }
    return () => timers.forEach((timer) => window.clearTimeout(timer));
    // players are intentionally read through positionKey/displayIndex to avoid restarting timers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionKey, displayIndex, board.length]);

  const activeTileIndex = active ? displayIndex[active.id] ?? active.currentTileIndex : null;
  const activeTile = active ? board[active.currentTileIndex] ?? null : null;
  const hoveredTile = hoverIndex !== null ? board[hoverIndex] ?? null : null;
  const preview = hoverIndex !== null ? store.previewTileImpact(hoverIndex) : null;

  return (
    <div className="flex h-full max-h-full min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden">
      {/* Compact mode: the whole ring becomes a fit-to-width SQUARE MINI-MAP — no
          panning, no scrolling. Tapping a cell opens the rich bottom-sheet detail. */}
      <div
        className={`relative ${
          compact ? 'mx-auto aspect-square w-full max-w-full' : 'h-full max-h-full min-h-0 w-full max-w-full'
        }`}
      >
        <div
          className="grid h-full max-h-full w-full"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${layout.rows}, minmax(0, 1fr))`,
            width: '100%',
            height: '100%',
            maxHeight: '100%',
          }}
        >
          {board.map((tile, index) => {
            const cell = layout.cells[index] ?? layout.cells[0];
            const isActiveTile = index === activeTileIndex;
            const isReachable = isHumanTurn && turnPhase === 'MOVEMENT_PREVIEW' && reachableIndices.includes(index);
            const isSelected = selectedTileIndex === index;
            const rent = rentFor(tile, market.phase);
            const heat = heatValue(tile, market.phase);
            const isCorner = cell.isCorner;
            const meta = DISTRICT_META[tile.district];
            // Colour-group progress for the human: [District · X/Total].
            const setProgress = human && tile.tileType === 'COMMERCIAL' ? districtSetOf(board, human.id, tile.district) : null;
            const districtColor = meta.bar;
            // Synchronised district glow: hovering or inspecting one tile lights up its whole colour group.
            const focusIndex = hoverIndex ?? inspectedIndex ?? store.inspectTileIndex;
            const focusDistrict = focusIndex !== null ? board[focusIndex]?.district ?? null : null;
            const districtSynced = Boolean(focusDistrict && focusDistrict === tile.district && focusIndex !== index);
            const surface = tile.isFrozen
              ? 'border-sky-500/60 bg-sky-900/40 text-slate-100'
              : heatmap
                ? heat > 300
                  ? 'border-emerald-600/60 bg-emerald-900/40 text-emerald-50'
                  : heat > 0
                    ? 'border-slate-600 bg-slate-800/80 text-slate-200'
                    : 'border-rose-700/60 bg-rose-950/50 text-rose-100'
                : `border-slate-600/70 bg-slate-800/95 text-slate-100 shadow-sm`;

            return (
              <button
                key={tile.id}
                type="button"
                onMouseEnter={() => setHoverIndex(index)}
                onFocus={() => setHoverIndex(index)}
                onMouseLeave={() => setHoverIndex(null)}
                onBlur={() => setHoverIndex(null)}
                onClick={() => {
                  if (isHumanTurn && turnPhase === 'MOVEMENT_PREVIEW' && isReachable) store.selectStopTile(index);
                  else if (tile.tileType !== 'COMMERCIAL') {
                    // Special tiles open the interactive "how this tile works" guide.
                    store.setInspectTile(null);
                    store.openTileGuide(tile.id);
                  } else {
                    // Only one inspector may be open: the local one wins, the sidebar one closes.
                    store.setInspectTile(null);
                    setInspectedIndex(index);
                  }
                }}
                style={{ gridColumn: cell.column, gridRow: cell.row }}
                className={`relative cursor-pointer p-0.5 text-left ${compact ? 'min-h-[64px] min-w-[72px]' : 'min-h-[74px] min-w-[82px]'}`}
              >
                <div
                  className={`flex h-full max-h-full w-full min-h-0 flex-col overflow-hidden rounded-xl border transition ${surface} ${
                    isCorner ? 'border-2' : ''
                  } ${compact ? 'p-1' : 'p-1.5'} ${isSelected ? 'ring-2 ring-amber-400' : ''} ${
                    isReachable && !isSelected ? 'ring-2 ring-emerald-500/80' : ''
                  } ${isActiveTile ? 'tile-active-glow' : ''} ${pulseTile === index ? 'rent-pulse' : ''} ${
                    districtSynced ? 'outline outline-2 outline-offset-1' : ''
                  } ${store.spotlightTileId === tile.id ? 'z-10 ring-4 ring-amber-400 ring-offset-2 animate-pulse' : ''}`}
                  style={{
                    // Colour-family border accent + synchronised district outline.
                    borderColor: tile.tileType === 'COMMERCIAL' && !tile.isFrozen && !heatmap ? `${districtColor}99` : undefined,
                    outlineColor: districtSynced ? districtColor : undefined,
                    // Compact mini-map: the district colour IS the cell background.
                    backgroundColor: compact && tile.tileType === 'COMMERCIAL' && !heatmap ? `${districtColor}26` : undefined,
                  }}
                >
                  {compact ? (
                    (() => {
                      // ---- Mini-map cell: icon + upgrade stars + ownership dots ----
                      const holders = players.filter((p) => (tile.shares[p.id] ?? 0) > 0);
                      const top = holders.map((p) => ({ p, pct: ownershipPct(tile, p.id) })).sort((a, b) => b.pct - a.pct)[0];
                      return (
                        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-0.5 overflow-hidden leading-none">
                          <span className="text-[13px] leading-none">{meta.icon}</span>
                          {tile.upgradeLevel > 0 && (
                            <span className="text-[8px] leading-none text-amber-300">{'★'.repeat(tile.upgradeLevel)}</span>
                          )}
                          {tile.isFrozen && <span className="text-[8px] leading-none text-cyan-200">❄</span>}
                          {holders.length > 0 && (
                            <div className="flex w-full flex-shrink-0 items-center justify-center gap-px overflow-hidden px-0.5">
                              {holders.slice(0, 4).map((p) => (
                                <span
                                  key={p.id}
                                  className="h-1 w-1.5 flex-shrink-0 rounded-[1px]"
                                  style={{ backgroundColor: p.color, opacity: top && top.pct >= 50 && top.p.id === p.id ? 1 : 0.55 }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                  <>
                  {/* District colour band + tinted card header: [Colour · X/Total]. */}
                  <div className="h-2 w-full shrink-0 rounded-sm" style={{ backgroundColor: districtColor }} />
                  <div
                    className="mt-px flex w-full shrink-0 items-center justify-center overflow-hidden rounded"
                    style={{ backgroundColor: `${districtColor}2e` }}
                    title={`${meta.label} — ${setProgress ? `${setProgress.owned}/${setProgress.total} controlled (${setProgress.complete ? 'monopoly complete — +25 % rent' : 'set in progress'})` : meta.label}`}
                  >
                    <span className="truncate px-1 text-[9px] font-black uppercase tracking-wider" style={{ color: districtColor }}>
                      {meta.icon} {meta.short}
                      {setProgress ? ` · ${setProgress.owned}/${setProgress.total}${setProgress.complete ? ' ★' : ''}` : ''}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-start gap-1">
                    <span className="min-w-0 flex-1 break-words font-bold leading-snug line-clamp-2 text-xs text-slate-100">
                      {meta.icon} {localizeTile(tile.name, t)}
                    </span>
                    <span className="hidden shrink-0 text-[9px] text-slate-500 xl:inline">#{index}</span>
                    {tile.isFrozen && (
                      <span className="shrink-0 rounded bg-cyan-500/30 px-1 text-[8px] font-bold text-cyan-100" title="Zoning freeze: no rent collection, no upgrades">
                        🧊 FROZEN
                      </span>
                    )}
                  </div>
                  <div className="whitespace-normal text-[10px] leading-snug text-slate-400">{tileSubtitle(tile, rent)}</div>
                  {tile.tileType === 'COMMERCIAL' && (
                    <div className="mt-0.5 flex items-center gap-1 whitespace-normal text-[10px] leading-snug">
                      <span className="font-bold text-amber-300">{money(tile.valuation > 0 ? tile.valuation : tile.baseCost)}</span>
                      <DeltaTag pct={tile.valuationDeltaPct} />
                    </div>
                  )}
                  {tile.tileType === 'COMMERCIAL' && (() => {
                    // Segmented equity bar: each holder's colour block sized by their
                    // ownership %, remaining float rendered dark grey.
                    const heldPct = players.reduce((sum, p) => sum + ownershipPct(tile, p.id), 0);
                    const floatPct = Math.max(0, 100 - heldPct);
                    const majority = players
                      .map((player) => ({ player, pct: ownershipPct(tile, player.id) }))
                      .filter((entry) => entry.pct >= 50)
                      .sort((a, b) => b.pct - a.pct)[0];
                    const initials = (name: string) =>
                      name.split(/\s+/).map((word) => word[0] ?? '').join('').slice(0, 2).toUpperCase();
                    return (
                      <>
                        <div className="h-2 w-full flex overflow-hidden rounded-full bg-slate-800">
                          {players.map((player) => {
                            const pct = ownershipPct(tile, player.id);
                            return pct > 0 ? (
                              <span
                                key={player.id}
                                style={{ width: `${pct}%`, backgroundColor: player.color }}
                                className="h-full"
                                title={`${player.name} ${pct.toFixed(1)}%`}
                              />
                            ) : null;
                          })}
                          {floatPct > 0 && (
                            <span
                              style={{ width: `${floatPct}%` }}
                              className="h-full bg-slate-600/60"
                              title={`Float ${floatPct.toFixed(0)}%`}
                            />
                          )}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1 overflow-hidden">
                          {majority && (
                            <span
                              className="shrink-0 rounded-full px-1.5 py-px text-[8px] font-black text-slate-950"
                              style={{ backgroundColor: majority.player.color, boxShadow: `0 0 10px ${majority.player.color}` }}
                              title={`${majority.player.name} controls ${majority.pct.toFixed(0)}%`}
                            >
                              👑 {majority.player.id === human?.id ? 'You' : initials(majority.player.name)} {Math.round(majority.pct)}%
                            </span>
                          )}
                          {tile.upgradeLevel > 0 && (
                            <span className="text-[8px] text-amber-300">{'★'.repeat(tile.upgradeLevel)}</span>
                          )}
                          {tile.isExpansionTile && (
                            <span className="rounded bg-violet-500/25 px-1 text-[8px] text-violet-100">NEW</span>
                          )}
                        </div>
                      </>
                    );
                  })()}
                  {isCorner && (
                    <span className="mt-auto text-[8px] uppercase tracking-widest text-amber-300/80">landmark</span>
                  )}
                  </>
                  )}
                </div>
              </button>
            );
          })}

          {/* Board centre = 2-column HUD: active decision on the left, permanent live
              player/bot ledger (cash in large mono type) on the right. */}
          <div
            className="hidden min-h-0 grid-cols-[minmax(0,1fr)_12rem] gap-2 rounded-2xl border border-slate-700/60 bg-slate-900/70 p-2 shadow-inner lg:grid"
            style={{ gridColumn: `2 / ${layout.columns}`, gridRow: `2 / ${layout.rows}` }}
          >
            <div className="flex min-h-0 flex-col gap-2.5 overflow-y-auto pr-1">
            {(() => {
              // ---- BOT TURN: dedicated full-panel screen, NO human CTAs may leak. ----
              const activePlayer = players[currentPlayerIndex];
              if (activePlayer?.isAI) {
                return (
                  <div className="flex h-full min-h-[220px] w-full flex-col items-center justify-center rounded-2xl border-2 border-slate-800 bg-slate-900/90 p-6 text-center shadow-xl backdrop-blur-md">
                    <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                      {t('board.botTurnNotice')}
                    </div>
                    <h2 className="text-3xl font-black tracking-tight text-amber-400 sm:text-4xl">
                      🤖 Hraje: {activePlayer.name}
                    </h2>
                    <div className="mt-4 flex items-center gap-2 text-sm text-slate-300">
                      <span className="relative flex h-3 w-3">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500"></span>
                      </span>
                      <span>{store.botStatus?.text ?? t('board.botPlaying')}</span>
                    </div>
                    <BotStatusBar />
                  </div>
                );
              }
              return (
                <>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300/80">Active decision</p>

            {activeTile && (
              <p className="text-[11px] text-slate-300">
                Standing on <span className="font-semibold text-amber-200">{activeTile.name}</span> ·{' '}
                {tileSubtitle(activeTile, rentFor(activeTile, market.phase))}
              </p>
            )}

            {hoveredTile && preview && (
              <div className="rounded-xl border border-slate-700/60 bg-slate-900/70 p-2 text-[11px]">
                <div className="font-semibold text-slate-200">
                  {hoveredTile.name} · +{preview.distance} tiles
                </div>
                <div className={preview.impact >= 0 ? 'text-emerald-300' : 'text-rose-300'}>{preview.label}</div>
                <div className="text-slate-400">
                  Push-your-luck next roll: safe {preview.safeChance}% · crash risk {100 - preview.safeChance}%
                </div>
                {idlePenalty > 0 && selectedTileIndex === hoverIndex && (
                  <div className="text-amber-300">Idle penalty {money(idlePenalty)}</div>
                )}
              </div>
            )}

            <TurnBanner />

            <BotStatusBar />

            <FastTravelPanel />

            <LobbyPanel />

            {/* Interactive buy/auction choice is human-only — unreachable while a bot plays. */}
            <OfferChoiceCard />
                </>
              );
            })()}

            <AuctionPanel />

            <div className="flex min-h-0 flex-1 flex-col" />
            </div>

            {/* Right column: permanent roster with live cash always in view. */}
            <div className="flex min-h-0 flex-col overflow-y-auto rounded-xl border border-slate-700/50 bg-slate-950/50 p-2">
              <PlayerLedgerHud />
            </div>
          </div>
        </div>

        {/* Bank / board anchor: destination or origin for tax, payroll and dividends. */}
        <span data-bank-anchor aria-hidden className="pointer-events-none absolute left-1/2 top-1 h-1 w-1 -translate-x-1/2" />

        {/* Flying-cash chips (fixed overlay: travels between ledger cards). */}
        <MoneyTransferOverlay />

        {/* Token layer: players grouped per tile as an overlapping avatar stack
            (mini-grid behavior via wrapping) so 5+ pawns never overflow a tile. */}
        <div className="pointer-events-none absolute inset-0 z-20">
          {groupedPawns.map(({ tileIndex, members }) => {
            const cell = layout.cells[tileIndex] ?? layout.cells[0];
            const crowded = members.length >= 6;
            const busy = members.length >= 4;
            return (
              <div
                key={tileIndex}
                className="absolute flex p-1 transition-[left,top] duration-300 ease-in-out"
                style={{
                  width: `${100 / layout.columns}%`,
                  height: `${100 / layout.rows}%`,
                  left: `${((cell.column - 1) * 100) / layout.columns}%`,
                  top: `${((cell.row - 1) * 100) / layout.rows}%`,
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                }}
              >
                <div className={`flex max-w-full flex-wrap ${crowded ? '-space-x-2' : busy ? '-space-x-1.5' : '-space-x-1'} ${busy ? 'justify-center' : ''}`}>
                  {members.map(({ player, shown, isStepping }) => (
                    <span
                      key={`${player.id}-${shown}`}
                      title={`${player.name}${player.isBankrupt ? ' (bankrupt)' : ''}${player.id === active?.id ? ' · ACTIVE' : ''}`}
                      className={busy ? 'origin-top scale-[0.82]' : ''}
                    >
                      <PlayerPawn
                        player={player}
                        isActive={player.id === active?.id}
                        isStepping={isStepping}
                      />
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {inspectedIndex !== null && board[inspectedIndex] && (
        <TileInspector
          tile={board[inspectedIndex]}
          tileIndex={inspectedIndex}
          onClose={closeInspector}
          onBuy={(t) => openTrade(t.id, 'BUY')}
          onSell={(t) => openTrade(t.id, 'SELL')}
        />
      )}
      {store.inspectTileIndex !== null && board[store.inspectTileIndex] && (
        <TileInspector
          tile={board[store.inspectTileIndex]}
          tileIndex={store.inspectTileIndex}
          onClose={closeInspector}
          onBuy={(t) => openTrade(t.id, 'BUY')}
          onSell={(t) => openTrade(t.id, 'SELL')}
        />
      )}
      {trade && <TradeSharesModal tile={board.find((t) => t.id === trade.tileId) ?? null} mode={trade.mode} onClose={() => setTrade(null)} />}
      <EmergencyModal />
      <TakeoverModal />
      <BlackSwanBanner />
      <AttentionModal />
      <TradeToast />
      <BotPurchaseToast />
      {rentToast && (
        <div key={rentToast.key} className="rent-toast pointer-events-none fixed left-1/2 top-6 z-[70] -translate-x-1/2">
          <div className="rent-toast-inner">
            <div className="rent-toast-glow flex items-center gap-2.5 rounded-full border border-emerald-400/70 bg-gradient-to-r from-emerald-500/25 to-teal-500/15 px-5 py-2.5 text-sm font-extrabold text-emerald-100 backdrop-blur">
              <span aria-hidden className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-400/25 text-base shadow-[0_0_12px_rgba(52,211,153,0.6)]">
                💵
              </span>
              <span>
                <span className="text-emerald-300">+{money(rentToast.amount)}</span> Rent received from {rentToast.from}!
              </span>
            </div>
          </div>
        </div>
      )}
      {cashFlash && human && (
        <div className="pointer-events-none fixed bottom-6 right-6 z-[70]">
          <div className="cash-flash rounded-2xl border border-emerald-400/60 bg-slate-950/90 px-4 py-2 text-sm font-extrabold">
            {money(human.cash)}
          </div>
        </div>
      )}
    </div>
  );
};

export default GameBoard;